import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Check, X, ChevronRight, ChevronDown, AlertTriangle, XCircle } from "lucide-react";
import { SCSection, fmtDate } from "./Common";
import {
    scAdminAmcUploads, scAdminAmcUploadDetail, scAdminApproveUpload, scAdminRejectUpload,
} from "../../../lib/sifcontrolApi";

const STATUS_STYLES = {
    draft: "bg-oth-bg-2 text-oth-ink3",
    validated: "bg-blue-100 text-blue-800",
    pending_admin: "bg-yellow-100 text-yellow-800",
    approved: "bg-green-100 text-green-800",
    rejected: "bg-red-100 text-red-800",
};

const StatusPill = ({ status }) => (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-body uppercase tracking-wider ${STATUS_STYLES[status] || "bg-oth-bg-2 text-oth-ink3"}`}>
        {status?.replace("_", " ") || "—"}
    </span>
);

const Row = ({ upload, onChange }) => {
    const [open, setOpen] = useState(false);
    const [detail, setDetail] = useState(null);
    const [busy, setBusy] = useState(false);
    const [note, setNote] = useState("");

    const toggle = async () => {
        const next = !open;
        setOpen(next);
        if (next && !detail) {
            try {
                const d = await scAdminAmcUploadDetail(upload.id);
                setDetail(d);
            } catch (e) { toast.error(e?.response?.data?.detail || "Could not load"); }
        }
    };

    const approve = async () => {
        if (!window.confirm("Approve this upload and apply changes to SIF masters?")) return;
        setBusy(true);
        try {
            await scAdminApproveUpload(upload.id, note);
            toast.success("Approved & applied.");
            onChange();
        } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
        finally { setBusy(false); }
    };

    const reject = async () => {
        if (!note.trim()) { toast.error("Add a reason before rejecting."); return; }
        if (!window.confirm("Reject this upload?")) return;
        setBusy(true);
        try {
            await scAdminRejectUpload(upload.id, note);
            toast.success("Rejected.");
            onChange();
        } catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
        finally { setBusy(false); }
    };

    const summary = upload.preview?.summary || {};
    const rows = detail?.preview?.rows || [];

    return (
        <div className="border-b border-oth-line/60 last:border-b-0" data-testid={`sc-amc-upload-row-${upload.id}`}>
            <div
                className="grid grid-cols-[1fr_140px_100px_130px_80px_80px_40px] items-center px-5 py-3 cursor-pointer hover:bg-oth-bg-2/40"
                onClick={toggle}
            >
                <div className="min-w-0">
                    <div className="text-[13px] font-body text-oth-ink truncate">{upload.amc_name || "AMC"}</div>
                    <div className="text-[11px] text-oth-ink3 truncate">{upload.filename}</div>
                </div>
                <div className="text-[11px] font-body text-oth-ink3">{fmtDate(upload.created_at)}</div>
                <div><StatusPill status={upload.status} /></div>
                <div className="text-[11px] font-body text-oth-ink3">
                    {summary.total ?? 0} rows · {summary.ok ?? 0}✓ {summary.warn ?? 0}⚠ {summary.error ?? 0}✗
                </div>
                <div className="text-[11px] text-oth-ink3">{upload.applied_count || 0} applied</div>
                <div className="text-[11px] text-oth-ink3">{upload.uploader_email?.split("@")[0]}</div>
                <div className="text-oth-ink3">{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</div>
            </div>
            {open && (
                <div className="px-5 py-4 bg-oth-bg-2/40 text-[12px] font-body text-oth-ink2 space-y-3" data-testid={`sc-amc-upload-detail-${upload.id}`}>
                    {!detail && <div className="text-oth-ink3">Loading…</div>}
                    {detail && rows.length === 0 && <div className="text-oth-ink3">No rows in preview.</div>}
                    {detail && rows.length > 0 && (
                        <div className="rounded-xl border border-oth-line/70 bg-white overflow-hidden">
                            <div className="max-h-[420px] overflow-auto thin-scroll">
                                <table className="w-full text-[12px]">
                                    <thead className="text-[10px] uppercase tracking-[0.16em] text-oth-ink3 bg-oth-bg-2/60">
                                        <tr>
                                            <th className="text-left px-4 py-2">SIF</th>
                                            <th className="text-left px-4 py-2">Status</th>
                                            <th className="text-left px-4 py-2">Changes / Issues</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r) => (
                                            <tr key={`${r.row_number}-${r.sif_code}`} className="border-t border-oth-line/70 align-top">
                                                <td className="px-4 py-2">
                                                    <div className="text-oth-ink">{r.sif_code || "—"}</div>
                                                    <div className="text-[11px] text-oth-ink3">{r.sif_name || ""}</div>
                                                </td>
                                                <td className="px-4 py-2">{r.status}</td>
                                                <td className="px-4 py-2 space-y-0.5">
                                                    {r.errors?.map((e, i) => (<div key={`e-${i}`} className="text-red-700 flex items-start gap-1"><XCircle size={11} className="mt-0.5 shrink-0" />{e}</div>))}
                                                    {r.warnings?.map((w, i) => (<div key={`w-${i}`} className="text-yellow-800 flex items-start gap-1"><AlertTriangle size={11} className="mt-0.5 shrink-0" />{w}</div>))}
                                                    {r.changes?.map((c, i) => (
                                                        <div key={`c-${i}`}>
                                                            <span className="text-oth-ink3">{c.field}:</span>{" "}
                                                            <span className="line-through text-oth-ink3">{String(c.old || "—")}</span>{" → "}
                                                            <span className="text-oth-ink">{String(c.new || "—")}</span>
                                                        </div>
                                                    ))}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    {upload.review_note && (
                        <div className="rounded-xl bg-white border border-oth-line/70 px-3 py-2">
                            <div className="text-[10px] uppercase tracking-[0.16em] text-oth-ink3">Existing note</div>
                            <div className="text-oth-ink2">{upload.review_note}</div>
                        </div>
                    )}
                    {upload.status === "pending_admin" && (
                        <div className="flex flex-wrap items-center gap-2 pt-1">
                            <input
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="Optional note (required if rejecting)"
                                className="h-9 flex-1 min-w-[220px] px-3 rounded-full border border-oth-line/70 bg-white text-xs focus:outline-none focus:border-oth-blue"
                                data-testid={`sc-amc-upload-note-${upload.id}`}
                            />
                            <button
                                onClick={approve}
                                disabled={busy}
                                className="btn-pill btn-pill-dark !text-xs !py-1.5 !px-3"
                                data-testid={`sc-amc-upload-approve-${upload.id}`}
                            >
                                <Check size={12} /> Approve & apply
                            </button>
                            <button
                                onClick={reject}
                                disabled={busy}
                                className="btn-pill !text-xs !py-1.5 !px-3 text-red-700 hover:bg-red-50"
                                data-testid={`sc-amc-upload-reject-${upload.id}`}
                            >
                                <X size={12} /> Reject
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default function SifControlAmcUploadsTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("pending_admin");

    const load = async () => {
        setLoading(true);
        try {
            const r = await scAdminAmcUploads({ status: statusFilter });
            setItems(r.items || []);
        } catch { toast.error("Could not load uploads"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

    return (
        <div data-testid="sc-admin-amc-uploads-tab">
            <SCSection
                title="AMC Uploads"
                subtitle="Review AMC-submitted SIF details before they update the SIF masters. Every approval snapshots the previous values."
                right={
                    <button onClick={load} className="btn-pill !text-xs !py-1.5 !px-3" data-testid="sc-amc-uploads-tab-refresh">
                        <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
                    </button>
                }
                testId="sc-amc-uploads-tab-header"
            />

            <div className="flex items-center gap-3 mb-4">
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="h-9 px-4 rounded-full border border-oth-line/70 bg-white text-xs focus:outline-none focus:border-oth-blue"
                    data-testid="sc-amc-uploads-tab-filter"
                >
                    <option value="pending_admin">Pending review</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="validated">Validated (draft)</option>
                    <option value="">All statuses</option>
                </select>
            </div>

            {loading ? (
                <div className="p-10 text-center text-sm font-body text-oth-ink3">Loading…</div>
            ) : items.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-oth-line/70 p-8 text-center text-sm font-body text-oth-ink3" data-testid="sc-amc-uploads-tab-empty">
                    Nothing here.
                </div>
            ) : (
                <div className="rounded-2xl border border-oth-line/70 bg-white overflow-hidden">
                    <div className="grid grid-cols-[1fr_140px_100px_130px_80px_80px_40px] text-[10px] uppercase tracking-[0.16em] text-oth-ink3 font-body px-5 py-3 bg-oth-bg-2/60 border-b border-oth-line/70">
                        <div>AMC · file</div>
                        <div>Created</div>
                        <div>Status</div>
                        <div>Rows</div>
                        <div>Applied</div>
                        <div>By</div>
                        <div />
                    </div>
                    {items.map((u) => <Row key={u.id} upload={u} onChange={load} />)}
                </div>
            )}
        </div>
    );
}
